// SPDX-License-Identifier: Apache-2.0
import { v4 as uuidv4 } from 'uuid';
import { Fill, RegularShape, Stroke, Style } from 'ol/style';
import { toRadians } from 'ol/math';
import { Circle as CircleGeom, Geometry } from 'ol/geom';
import Feature from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import IGirafeContext from '../context/icontext';
import { getAreaAsMetricText, getAzimuthAsText, getLengthAsMetricText } from '../utils/olutils';
import { Coordinate } from 'ol/coordinate';
import DrawingState from './drawingState';
import { fromCircle } from 'ol/geom/Polygon';
import LineString from 'ol/geom/LineString';

// New shapes must be appended at the end, as the numeric values are used to serialize features.
export enum DrawingShape {
  Point,
  Polyline,
  Polygon,
  Square,
  Rectangle,
  Disk,
  FreehandPolyline,
  FreehandPolygon,
  Text
}

export type ArrowStyle = 'none' | 'start' | 'end' | 'both';
export type ArrowPosition = 'whole' | 'each' | 'mid';
export type LineStroke = 'full' | 'dash' | 'dot' | 'double';
export type MeasureInformation = 'none' | 'simple' | 'full';

export type SerializedFeature = {
  n: string;
  sc: string;
  sw: number;
  fc: string;
  ls: string;
  as: string;
  ap: string;
  nfz: number;
  mfz: number;
  f: string;
  g: object;
  dn: boolean;
  mi: string;
  nc: string;
  mc: string;
  s: boolean;
  t: DrawingShape;
};

export default class DrawingFeature {
  public static readonly geoJSONFormat = new GeoJSON();
  private readonly _tool: DrawingShape;
  private _name: string;
  private _nameColor: string;
  private _strokeColor: string;
  private _strokeWidth: number;
  private _fillColor: string;
  private _lineStroke: LineStroke = 'full';
  private _arrowStyle: ArrowStyle = 'none';
  private _arrowPosition: ArrowPosition = 'whole';
  private _measureInformation: MeasureInformation = 'simple';
  private _nameFontSize: number;
  private _measureFontSize: number;
  private _measureColor: string;
  private _font: string;
  private _displayName = true;
  private _selected = false;

  public geojson: object = {};
  public id: string = uuidv4();
  public onChange: (f: DrawingFeature) => void = () => {};

  private readonly drawingState: DrawingState;
  private readonly defaultDrawingConfig: any;

  public constructor(tool: DrawingShape, drawingState: DrawingState, drawingConfig: any) {
    this.defaultDrawingConfig = drawingConfig;
    this.drawingState = drawingState;
    this._tool = tool;
    this._name = this.getDefaultName(DrawingShape[tool]);
    this._strokeColor = this.defaultDrawingConfig.defaultStrokeColor;
    this._strokeWidth = this.defaultDrawingConfig.defaultStrokeWidth;
    this._fillColor = this.defaultDrawingConfig.defaultFillColor;
    this._nameFontSize = this.defaultDrawingConfig.defaultTextSize;
    this._measureFontSize = this.defaultDrawingConfig.defaultTextSize;
    this._font = this.defaultDrawingConfig.defaultFont;
    this._nameColor = '#000000';
    this._measureColor = '#000000';
  }

  public get name() {
    return this._name;
  }
  public set name(v) {
    this._name = v;
    this.onChange(this);
  }

  public get strokeColor() {
    return this._strokeColor;
  }
  public set strokeColor(v) {
    this._strokeColor = v;
    this.onChange(this);
  }

  public get strokeWidth() {
    return this._strokeWidth;
  }
  public set strokeWidth(v) {
    this._strokeWidth = v;
    this.onChange(this);
  }

  public get lineStroke() {
    return this._lineStroke;
  }
  public set lineStroke(v) {
    this._lineStroke = v;
    this.onChange(this);
  }

  public get fillColor() {
    return this._fillColor;
  }
  public set fillColor(v) {
    this._fillColor = v;
    this.onChange(this);
  }

  public get arrowStyle() {
    return this._arrowStyle;
  }
  public set arrowStyle(v) {
    this._arrowStyle = v;
    this.onChange(this);
  }

  public get arrowPosition() {
    return this._arrowPosition;
  }
  public set arrowPosition(v) {
    this._arrowPosition = v;
    this.onChange(this);
  }

  public get nameFontSize() {
    return this._nameFontSize;
  }
  public set nameFontSize(v) {
    this._nameFontSize = v;
    this.onChange(this);
  }

  public get measureFontSize() {
    return this._measureFontSize;
  }
  public set measureFontSize(v) {
    this._measureFontSize = v;
    this.onChange(this);
  }

  public get font() {
    return this._font;
  }
  public set font(v) {
    this._font = v;
    this.onChange(this);
  }

  public get displayName() {
    return this._displayName;
  }
  public set displayName(v) {
    this._displayName = v;
    this.onChange(this);
  }

  public get measureInformation(): MeasureInformation {
    return this._measureInformation;
  }

  public set measureInformation(v: MeasureInformation) {
    this._measureInformation = v;
    this.onChange(this);
  }

  public get nameColor() {
    return this._nameColor;
  }
  public set nameColor(v) {
    this._nameColor = v;
    this.onChange(this);
  }

  public get measureColor() {
    return this._measureColor;
  }
  public set measureColor(v) {
    this._measureColor = v;
    this.onChange(this);
  }

  public get type() {
    return this._tool;
  }

  public get selected() {
    return this._selected;
  }

  public set selected(v) {
    this._selected = v;
    this.onChange(this);
  }

  public addToState() {
    this.drawingState.features.push(this);
  }

  public serialize(): SerializedFeature {
    return {
      n: this._name,
      sc: this._strokeColor,
      sw: this._strokeWidth,
      ls: this._lineStroke,
      as: this._arrowStyle,
      ap: this._arrowPosition,
      fc: this._fillColor,
      nfz: this._nameFontSize,
      mfz: this._measureFontSize,
      f: this._font,
      g: this.geojson,
      t: this._tool,
      dn: this._displayName,
      mi: this._measureInformation,
      nc: this._nameColor,
      mc: this._measureColor,
      s: this._selected
    };
  }

  public getLengthText(length: number) {
    return getLengthAsMetricText(this.measureInformation === 'none' ? undefined : length);
  }

  public getAreaText(area: number) {
    return getAreaAsMetricText(this.measureInformation === 'none' ? undefined : area);
  }

  public getCoordText(coord: number[]) {
    if (this.measureInformation === 'none') {
      return '';
    }

    if (coord.length > 2) {
      return 'E ' + coord[0].toFixed(2) + '\nN ' + coord[1].toFixed(2) + '\nH ' + coord[2].toFixed(2);
    }
    return 'E ' + coord[0].toFixed(2) + '\nN ' + coord[1].toFixed(2);
  }

  public getAzimuthText(circle: CircleGeom) {
    return getAzimuthAsText(
      this.measureInformation === 'none' ? undefined : ((circle.getProperties()['azimuth'] as number) ?? undefined)
    );
  }

  public isPointPolylineOrText() {
    return (
      this.type === DrawingShape.Point ||
      this.type === DrawingShape.Polyline ||
      this.type === DrawingShape.FreehandPolyline ||
      this.type === DrawingShape.Text
    );
  }

  public getVertexStyle(activeNode = false): Style {
    const defaultConfig = this.defaultDrawingConfig;
    return new Style({
      zIndex: 1002,
      image: new RegularShape({
        points: 4,
        radius: activeNode ? defaultConfig.defaultVertexRadius + 2 : defaultConfig.defaultVertexRadius,
        angle: toRadians(45),
        rotateWithView: false,
        fill: new Fill({
          color: defaultConfig.defaultVertexFillColor
        }),
        stroke: new Stroke({
          color: this.strokeColor,
          width: activeNode ? defaultConfig.defaultVertexStrokeWidth + 2 : defaultConfig.defaultVertexStrokeWidth
        })
      })
    });
  }

  /**
   * Creates a new DrawingFeature from an ol feature
   */
  public static createFromOlFeature(
    shape: DrawingShape,
    olFeature: Feature<Geometry>,
    context: IGirafeContext,
    drawingState: DrawingState
  ): DrawingFeature {
    const newFeature = new DrawingFeature(shape, drawingState, context.configManager.Config.drawing);
    newFeature.geojson = DrawingFeature.geojsonFromOlFeature(olFeature, shape);
    newFeature.addToState();
    return newFeature;
  }

  public static deserialize(serializedFeature: SerializedFeature, context: IGirafeContext, drawingState: DrawingState) {
    const newFeature = new DrawingFeature(serializedFeature.t, drawingState, context.configManager.Config.drawing);
    newFeature.geojson = serializedFeature.g;
    newFeature.name = serializedFeature.n;
    newFeature.strokeColor = serializedFeature.sc;
    newFeature.strokeWidth = serializedFeature.sw;
    newFeature.lineStroke = serializedFeature.ls as LineStroke;
    newFeature.arrowStyle = serializedFeature.as as ArrowStyle;
    newFeature.arrowPosition = serializedFeature.ap as ArrowPosition;
    newFeature.fillColor = serializedFeature.fc;
    newFeature.nameFontSize = serializedFeature.nfz;
    newFeature.measureFontSize = serializedFeature.mfz;
    newFeature.font = serializedFeature.f;
    newFeature.displayName = serializedFeature.dn;
    newFeature.measureInformation = serializedFeature.mi as MeasureInformation;
    newFeature.nameColor = serializedFeature.nc;
    newFeature.measureColor = serializedFeature.mc;
    newFeature.selected = serializedFeature.s;
    newFeature.addToState();
    return newFeature;
  }

  public static circleToPolygon(center: number[], radius: number, nbEdges = 300) {
    const positions: number[][] = [];
    for (let i = 0; i < 2 * Math.PI; i += (2 * Math.PI) / nbEdges) {
      positions.push([center[0] + radius * Math.cos(i), center[1] + radius * Math.sin(i)]);
    }
    return [...positions, positions[0]];
  }

  public static geojsonFromOlFeature(olFeature: Feature<Geometry>, shapeType: DrawingShape): object {
    if (shapeType === DrawingShape.Disk) {
      const disk = olFeature.getGeometry()! as CircleGeom;
      const center = disk.getCenter();
      const radius = disk.getRadius();
      const pointer = (disk.getProperties()['pointer'] as Coordinate) ?? [center[0] + radius, center[1]];
      return {
        id: olFeature.getId(),
        type: 'Feature',
        geometry: {
          type: 'Disk',
          center: center,
          radius: radius,
          azimuth: (disk.get('azimuth') as number) ?? 0,
          pointer: pointer
        }
      };
    }
    return JSON.parse(DrawingFeature.geoJSONFormat.writeFeature(olFeature));
  }

  /**
   * Create an ol feature instance from a geojson object, optionally simplifying circle geometries to polygons
   * with a limited number of edges to improve support across formats and services.
   */
  public static olFeatureFromGeoJson(geoJson: any, circleToPolygonNbEdges?: number): Feature<Geometry> {
    const geometry = geoJson.geometry;
    // Handle custom "Disk" geometry type
    if (geometry?.type === 'Disk') {
      if (!(Array.isArray(geometry.center) && geometry.center.length >= 2 && typeof geometry.radius === 'number')) {
        throw new Error('Invalid Disk geometry format');
      }
      let olGeometry: Geometry = new CircleGeom(geometry.center, geometry.radius);
      if (circleToPolygonNbEdges) {
        olGeometry = fromCircle(olGeometry as CircleGeom, circleToPolygonNbEdges);
      }
      olGeometry.setProperties({
        azimuth: geometry.azimuth,
        pointer: geometry.pointer
      });
      const feature = new Feature(olGeometry);
      if (geoJson.id) {
        feature.setId(geoJson.id);
      }
      return feature;
    } else {
      // Standard case: parse with GeoJSON format reader
      const feature = new Feature(DrawingFeature.geoJSONFormat.readFeatures(geoJson)[0].getGeometry());
      if (Array.isArray(feature)) {
        throw new TypeError('GeoJSON feature is an array, expected single feature');
      }
      return feature;
    }
  }

  /**
   * Transform a geojson (valid object, or disk with its properties) to a new projection.
   * @returns The transformed geojson object or null.
   */
  public static transformGeoJson(geoJson: object, oldProj: string, newProj: string): object | null {
    if (oldProj === null || oldProj === newProj) {
      return geoJson;
    }
    try {
      type GeoJsonWithType = { geometry?: { type?: string } };
      if ((geoJson as GeoJsonWithType).geometry?.type === 'Disk') {
        // Case of disks that are not a valid geojson object, handle them manually.
        const olFeature = DrawingFeature.olFeatureFromGeoJson(geoJson);
        const geom = olFeature.getGeometry() as CircleGeom | undefined;
        geom?.transform(oldProj, newProj);
        if (geom) {
          const center = geom.getCenter();
          const line = new LineString([center, [center[0], center[1] + geom.getRadius()]]);
          line.rotate(toRadians((geom.get('azimuth') ?? 0) * -1), center);
          geom.set('pointer', line.getLastCoordinate());
        }
        return DrawingFeature.geojsonFromOlFeature(olFeature, DrawingShape.Disk);
      }
      // Case of valid geojson objects.
      return DrawingFeature.geoJSONFormat.writeFeatureObject(
        DrawingFeature.geoJSONFormat.readFeature(geoJson, {
          dataProjection: oldProj,
          featureProjection: newProj
        }) as Feature<Geometry>
      );
    } catch (e: unknown) {
      console.error('Error transforming this draw object to the new projection.', e);
    }
    return null;
  }

  /**
   * Finds a name composed of the shape type and a number.
   * The number starts from how many shapes of the same type are already in the state +1.
   * Then checks if such a candidate name already exists and increments if so.
   * @param geometryTypename
   * @returns
   */
  private getDefaultName(geometryTypename: string): string {
    const shapeTypeId = DrawingShape[geometryTypename as keyof typeof DrawingShape];
    const drawingFeaturesSameType = this.drawingState.features.filter((feat) => feat.type === shapeTypeId);

    const existingNames = drawingFeaturesSameType.map((f) => f.name.toLowerCase().trim());
    let counter = existingNames.length + 1;

    while (true) {
      const nameCandidate = `${geometryTypename} ${counter}`;

      if (existingNames.includes(nameCandidate.toLowerCase())) {
        counter++;
      } else {
        return nameCandidate;
      }
    }
  }
}
